import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { CustomerListRow } from '@domain/customer/Customer';
import { listCustomers } from '@application/customer/listCustomers';
import { apiClient } from '@infrastructure/api/client';
import { HttpCustomerRepository } from '@infrastructure/api/HttpCustomerRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { StatusBadge } from '@app/components/StatusBadge';

const repo = new HttpCustomerRepository(apiClient);
const list = listCustomers(repo);

const columns: DataTableColumn<CustomerListRow>[] = [
  { id: 'email', header: 'Email', accessor: 'email' },
  { id: 'company_name', header: 'Company', accessor: 'company_name' },
  { id: 'status', header: 'Status', accessor: (r) => <StatusBadge value={r.status} /> },
  { id: 'plan_name', header: 'Plan', accessor: 'plan_name' },
  { id: 'balance', header: 'Balance', accessor: (r) => `$${r.balance}` },
  {
    id: 'messages_sent_month',
    header: 'Msgs / mo',
    accessor: 'messages_sent_month',
  },
];

export function SubscribersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const filters = useMemo(() => {
    const f: Record<string, string> = {};
    if (search) f.search = search;
    if (statusFilter) f.status = statusFilter;
    return f;
  }, [search, statusFilter]);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-lg font-semibold">Subscribers</h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <input
            type="search"
            placeholder="Search email or company"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded border px-2 py-1"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border px-2 py-1"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>
      <DataTable<CustomerListRow>
        columns={columns}
        fetcher={(p) =>
          list({
            limit: p.limit,
            offset: p.offset,
            sort: p.sort,
            search: filters.search,
            status: filters.status,
          })
        }
        pagination="offset"
        defaultPageSize={25}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/subscribers/${r.id}`)}
        emptyState={<span data-testid="datatable-empty">No subscribers match.</span>}
      />
    </section>
  );
}
